import React, { useState } from 'react';

import { Button, Dialog, Typography } from '../../../../../UIKit';
import { useLanguage, useNavigation } from '../../../../../Hooks';
import { useRequest } from '../../../../../Hooks/useRequest';
import { IMPORT_URLS } from '../../../../../Constants/Urls.constants';
import { useSelector } from 'react-redux';
import { selectCurrentProvider } from '../../../../../store/provider';
import { selectCurrentImport } from '../../../../../store/imports';
import { IImport } from '../../../../../Models/DTO/Import.model';
import { Route } from '~/Constants/Routes.constants';
import { makeStyles } from '~/UIKit/Style';

interface StartImportModalProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  content: {
    padding: '50px 60px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    lineHeight: '22px',
    textAlign: 'center',
  },
  info: {
    textAlign: 'center',
    margin: '30px auto',
    height: '22px',
    width: '321px',
    fontSize: '16px',
    letterSpacing: '0.5px',
    lineHeight: '22px',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    placeItems: 'center',
    alignItems: 'center',
    marginTop: 30,
    '& > button': {
      width: 250,
      '&:not(:last-child)': {
        marginBottom: 20,
      },
      '&:last-child': {
        textDecoration: 'underline',
      },
    },
  },
});

const useStartImportModal = ({ onClose }: { onClose: () => void }) => {
  const { request } = useRequest();
  const currentProvider = useSelector(selectCurrentProvider);
  const currentImport = useSelector(selectCurrentImport);
  const { navigate } = useNavigation();

  const [submitting, setSubmitting] = useState(false);

  const handleStartImport = async () => {
    setSubmitting(true);
    await request<IImport>({
      url: IMPORT_URLS.IMPORT_PROCESS_RUN,
      method: 'POST',
      query: {
        providerId: currentProvider?.uuid,
        importId: currentImport?.id,
      },
    });
    setSubmitting(false);
    onClose();
    navigate(Route.IMPORT_COMPLETE);
  };

  return {
    submitting,
    handleStartImport,
  };
};

const StartImportModal = ({ open, onClose }: StartImportModalProps) => {
  const { classes } = useStyles();
  const { t } = useLanguage();
  const { handleStartImport, submitting } = useStartImportModal({ onClose });

  return (
    <Dialog onClose={onClose} open={open}>
      <div className={classes.content}>
        <h2 className={classes.title} id="dialog-title">
          {t('app.import.start')}
        </h2>
        <Typography className={classes.info}>{t('app.import.start.text')}</Typography>
        <div className={classes.buttons}>
          <Button
            color="primary"
            onClick={handleStartImport}
            variant="contained"
            isLoading={submitting}
          >
            {t('app.submit')}
          </Button>
          <Button onClick={onClose}>{t('app.cancel')}</Button>
        </div>
      </div>
    </Dialog>
  );
};

export default StartImportModal;
